import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { CaptainDTO } from '../../types';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import {
  CAPTAIN_MAX_HEALTH_ADVANCES,
  CAPTAIN_MAX_WILL_ADVANCES,
  countCaptainAdvances,
  getCaptainLevel,
} from '../../utils/captainRules';

interface CaptainLevelUpModalProps {
  open: boolean;
  captain: CaptainDTO;
  onClose: () => void;
}

export const CaptainLevelUpModal: React.FC<CaptainLevelUpModalProps> = ({ open, captain, onClose }) => {
  const tricks = useReferenceDataStore((s) => s.tricks);
  const applyCaptainAdvance = useDirtyTrackingStore((s) => s.applyCaptainAdvance);

  const [advanceType, setAdvanceType] = React.useState<'trick' | 'health' | 'will'>('trick');
  const [selectedTrickId, setSelectedTrickId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setAdvanceType('trick');
      setSelectedTrickId(null);
    }
  }, [open]);

  const availableTricks = tricks.filter((t) => !captain.trickIds.includes(t.id));
  const healthAdvancesTaken = countCaptainAdvances(captain.advances, 'health');
  const willAdvancesTaken = countCaptainAdvances(captain.advances, 'will');
  const healthAvailable = healthAdvancesTaken < CAPTAIN_MAX_HEALTH_ADVANCES;
  const willAvailable = willAdvancesTaken < CAPTAIN_MAX_WILL_ADVANCES;

  const canConfirm =
    advanceType === 'trick' ? selectedTrickId !== null
      : advanceType === 'health' ? healthAvailable
        : willAvailable;

  const handleConfirm = () => {
    if (!canConfirm) return;
    applyCaptainAdvance(advanceType, advanceType === 'trick' ? selectedTrickId ?? undefined : undefined);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Captain Level Up (to level {getCaptainLevel(captain.advances) + 1})</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <RadioGroup
          value={advanceType}
          onChange={(e) => setAdvanceType(e.target.value as 'trick' | 'health' | 'will')}
        >
          <FormControlLabel
            value="trick"
            control={<Radio size="small" />}
            label="Learn a new Trick of the Trade"
            disabled={availableTricks.length === 0}
          />
          <FormControlLabel
            value="health"
            control={<Radio size="small" />}
            label={`+1 Health (${healthAdvancesTaken}/${CAPTAIN_MAX_HEALTH_ADVANCES} taken)`}
            disabled={!healthAvailable}
          />
          <FormControlLabel
            value="will"
            control={<Radio size="small" />}
            label={`+1 Will (${willAdvancesTaken}/${CAPTAIN_MAX_WILL_ADVANCES} taken)`}
            disabled={!willAvailable}
          />
        </RadioGroup>

        {advanceType === 'trick' && (
          availableTricks.length > 0 ? (
            <List dense>
              {availableTricks.map((trick) => (
                <ListItemButton
                  key={trick.id}
                  selected={selectedTrickId === trick.id}
                  onClick={() => setSelectedTrickId(trick.id)}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {selectedTrickId === trick.id ? <CheckCircleIcon color="primary" /> : <RadioButtonUncheckedIcon />}
                  </ListItemIcon>
                  <ListItemText
                    primary={trick.name}
                    secondary={`${trick.effect} — Declare: ${trick.whenToDeclare}`}
                  />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Alert severity="info">The captain already knows every trick of the trade.</Alert>
          )
        )}

        <Typography variant="body2" color="textSecondary">
          Each level-up costs 100 experience. A new trick must be one the captain does not already know.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={!canConfirm}>
          Apply Level Up
        </Button>
      </DialogActions>
    </Dialog>
  );
};
